/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 22, 2026 by hoarfroster
 *
 */

export class GoodDate {
    date: Date;

    constructor(date?: Date) {
        this.date = date ?? new Date();
    }

    static fromEpochSeconds(epoch: number): GoodDate {
        return new GoodDate(new Date(epoch * 1000));
    }

    toEpochSeconds(): number {
        return Math.floor(this.date.getTime() / 1000);
    }

    static nowInEpochSeconds(): number {
        return Math.floor(new Date().getTime() / 1000);
    }

    toInputTimeString(): string {
        const offsetDate = new Date(this.date.getTime() - this.date.getTimezoneOffset() * 60000);
        return offsetDate.toISOString().split('T')[1].split('.')[0].slice(0, 5);
    }

    toInputDateString(): string {
        // accepted: yyyy-mm-ddThh:mm
        const epoch = this.date.getTime(),
            offset = epoch - this.date.getTimezoneOffset() * 60000;
        const offsetDate = new Date(offset > 0 ? offset : 0); // prevent negative epoch
        return offsetDate.toISOString().slice(0, 16);
    }

    static fromString(date: string): GoodDate {
        // Interpret datetime-local strings as local wall time.
        // Avoid implicit UTC parsing differences across runtimes.
        const dtLocal = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(date);
        if (dtLocal) {
            const [, y, m, d, hh, mm, ss] = dtLocal;
            return new GoodDate(
                new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), ss ? Number(ss) : 0, 0)
            );
        }

        const dLocal = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
        if (dLocal) {
            const [, y, m, d] = dLocal;
            return new GoodDate(new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0, 0));
        }

        return new GoodDate(new Date(date));
    }

    getTime(): number {
        return this.date.getTime();
    }

    getTimezoneOffset(): number {
        return this.date.getTimezoneOffset();
    }

    add(d: number, type: 'year' | 'month' | 'week' | 'date'): GoodDate {
        const newDate = new Date(this.date);
        switch (type) {
            case 'year':
                newDate.setFullYear(newDate.getFullYear() + d);
                break;
            case 'month':
                newDate.setMonth(newDate.getMonth() + d);
                break;
            case 'week':
                newDate.setDate(newDate.getDate() + d * 7);
                break;
            case 'date':
                newDate.setDate(newDate.getDate() + d);
                break;
        }
        return new GoodDate(newDate);
    }

    startOfWeek(): GoodDate {
        return this.add(0 - this.date.getDay(), 'date');
    }

    weekDays(): GoodDate[] {
        const w = this.startOfWeek();
        return Array.from({ length: 7 }).map((_, idx) => w.add(idx, 'date'));
    }

    format(fmt: string): string {
        return fmt
            .replace(/YYYY/g, this.date.getFullYear().toString())
            .replace(/YY/g, this.date.getFullYear().toString().slice(-2))
            .replace(/MMM/g, this.date.toLocaleDateString('en-US', { month: 'short' }))
            .replace(/MM/g, (this.date.getMonth() + 1).toString().padStart(2, '0'))
            .replace(/DD/g, this.date.getDate().toString().padStart(2, '0'))
            .replace(/HH/g, this.date.getHours().toString().padStart(2, '0'))
            .replace(/mm/g, this.date.getMinutes().toString().padStart(2, '0'))
            .replace(/ss/g, this.date.getSeconds().toString().padStart(2, '0'));
    }

    toISOString() {
        return this.date.toISOString();
    }

    toLocalizedString() {
        return this.date.toLocaleString();
    }

    toLocalizedTimeString() {
        return this.date.toLocaleTimeString();
    }

    toLocalizedDateString() {
        return this.date.toLocaleDateString();
    }

    toISODate() {
        return this.toISOString().split('T')[0];
    }

    toJSON(): number {
        return this.toEpochSeconds();
    }

    getDaysFromNow() {
        const today = new GoodDate();
        const diffTime = Math.abs(this.getTime() - today.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    toDescriptiveDate() {
        return getDescriptiveDate(this.date);
    }
}

export const getCurrentTimeInISO = () => new Date().toISOString();
export const getDescriptiveDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    if (diff < 1000) {
        return 'Just now';
    } else if (diff < 1000 * 60) {
        return `${Math.floor(diff / 1000)} seconds ago`;
    } else if (diff < 1000 * 60 * 60) {
        return `${Math.floor(diff / (1000 * 60))} minutes ago`;
    } else if (diff < 1000 * 60 * 60 * 24) {
        return `${Math.floor(diff / (1000 * 60 * 60))} hours ago`;
    } else if (diff < 1000 * 60 * 60 * 24 * 7) {
        return `${Math.floor(diff / (1000 * 60 * 60 * 24))} days ago`;
    } else {
        return date.toLocaleDateString();
    }
};

export const getInputDate = (date: Date) => {
    // accepted: yyyy-mm-dd
    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offsetDate.toISOString().split('T')[0];
};

export const getInputTime = (date: Date) => {
    // accepted: hh:mm
    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offsetDate.toISOString().split('T')[1].split('.')[0].slice(0, 5);
};

export const getInputDatetime = (date: Date) => {
    // accepted: yyyy-mm-ddThh:mm
    const epoch = date.getTime(),
        offset = epoch - date.getTimezoneOffset() * 60000;
    const offsetDate = new Date(offset > 0 ? offset : 0); // prevent negative epoch
    return offsetDate.toISOString().slice(0, 16);
};
